import { supabase } from '../lib/supabase';

const MAX_PLAYERS = 12;
const MAX_OFFICIALS = 3;
const BUCKET = 'region-associations';

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
];

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const resolveStorageUrl = (url, path) => {
  if (url && typeof url === 'string' && url.trim()) return url.trim();
  if (path && typeof path === 'string' && path.trim()) {
    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET).getPublicUrl(path.trim());
    return publicUrl || null;
  }
  return null;
};

const normalizePlayer = (row) => ({
  id: row.id,
  regionalTeamId: row.regional_team_id,
  fullName: row.full_name,
  jerseyNumber: row.jersey_number,
  position: row.position,
  photoUrl: resolveStorageUrl(row.photo_url, row.photo_path),
  photoPath: row.photo_path,
  dateOfBirth: row.date_of_birth,
  age: row.age,
  email: row.email,
  phone: row.phone,
  birthCertificateUrl: resolveStorageUrl(row.birth_certificate_url, row.birth_certificate_path),
  birthCertificatePath: row.birth_certificate_path,
  createdAt: row.created_at,
});

const normalizeOfficial = (row) => ({
  id: row.id,
  regionalTeamId: row.regional_team_id,
  fullName: row.full_name,
  roleTitle: row.role_title,
  email: row.email,
  phone: row.phone,
  createdAt: row.created_at,
});

/**
 * Convert WebP/HEIC and other storage-unsupported image types to JPEG.
 * Supabase buckets often reject image/webp even when the app allows it.
 */
async function normalizeImageForUpload(file) {
  if (!file || !file.type?.startsWith('image/')) return file;
  if (['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    if (typeof bitmap.close === 'function') bitmap.close();

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) return file;

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    console.warn('Image conversion failed, uploading original file:', err);
    return file;
  }
}

async function uploadRosterFile(file, teamId, folder, allowedTypes, maxBytes, label) {
  if (!file) return null;

  let uploadFile = file;
  if (file.type?.startsWith('image/')) {
    uploadFile = await normalizeImageForUpload(file);
  }

  const mimeOk =
    allowedTypes.includes(uploadFile.type) ||
    (uploadFile.type?.startsWith('image/') &&
      allowedTypes.some((type) => type.startsWith('image/')));

  if (!mimeOk) {
    throw new Error(`${label} must be a valid file type`);
  }
  if (uploadFile.size > maxBytes) {
    throw new Error(`${label} must be less than ${Math.round(maxBytes / (1024 * 1024))}MB`);
  }

  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 11);
  const sanitizedName = uploadFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const ext = sanitizedName.split('.').pop() || (uploadFile.type === 'image/jpeg' ? 'jpg' : 'bin');
  const nameWithoutExt = sanitizedName.substring(0, sanitizedName.lastIndexOf('.')) || sanitizedName;
  const filename = `${timestamp}-${randomStr}-${nameWithoutExt}.${ext}`;
  const filePath = `regional-teams/${teamId}/${folder}/${filename}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, uploadFile, {
      cacheControl: '3600',
      upsert: true,
      contentType: uploadFile.type || 'image/jpeg',
    });

  if (uploadError) {
    const message = uploadError.message || `Failed to upload ${label}`;
    if (message.toLowerCase().includes('mime type') || message.toLowerCase().includes('not supported')) {
      throw new Error(
        `${label} upload blocked by storage MIME settings. Run migration 20260719000003_fix_region_association_storage_uploads.sql in Supabase, then try again.`
      );
    }
    if (message.includes('Bucket not found')) {
      throw new Error(
        `Storage bucket "${BUCKET}" not found. Create it in Supabase Storage, then try again.`
      );
    }
    throw new Error(message);
  }

  const {
    data: { publicUrl },
  } = supabase.storage.from(BUCKET).getPublicUrl(filePath);

  return { path: filePath, url: publicUrl };
}

export const regionalTeamRosterService = {
  MAX_PLAYERS,
  MAX_OFFICIALS,

  async getPlayers(regionalTeamId) {
    const { data, error } = await supabase
      .from('regional_team_players')
      .select('*')
      .eq('regional_team_id', regionalTeamId)
      .order('full_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional team players');
    return (data || []).map(normalizePlayer);
  },

  async getPlayerById(playerId) {
    if (!playerId) return null;

    const { data, error } = await supabase
      .from('regional_team_players')
      .select(`
        *,
        regional_team:regional_teams(
          id,
          team_name,
          logo_url,
          logo_path,
          region,
          registration_status
        )
      `)
      .eq('id', playerId)
      .single();

    if (error) throw new Error(error.message || 'Failed to load regional player');

    const player = normalizePlayer(data);
    const team = data.regional_team;
    return {
      ...player,
      teamId: team?.id || data.regional_team_id,
      teamName: team?.team_name || 'Unknown Team',
      teamLogoUrl: resolveStorageUrl(team?.logo_url, team?.logo_path),
      teamRegion: team?.region || null,
      teamStatus: team?.registration_status || null,
    };
  },

  async getAllPlayers() {
    const { data, error } = await supabase
      .from('regional_team_players')
      .select(`
        *,
        regional_team:regional_teams(
          id,
          team_name,
          logo_url,
          logo_path,
          registration_status,
          region
        )
      `)
      .order('full_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional team players');

    return (data || []).map((row) => {
      const player = normalizePlayer(row);
      const team = row.regional_team;
      return {
        ...player,
        teamId: team?.id || row.regional_team_id,
        teamName: team?.team_name || 'Unknown Team',
        teamLogoUrl: resolveStorageUrl(team?.logo_url, team?.logo_path),
        teamStatus: team?.registration_status || null,
        teamRegion: team?.region || null,
      };
    });
  },

  async getAllOfficials() {
    const { data, error } = await supabase
      .from('regional_team_officials')
      .select(`
        *,
        regional_team:regional_teams(
          id,
          team_name,
          region,
          registration_status
        )
      `)
      .order('full_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional team officials');

    return (data || []).map((row) => {
      const official = normalizeOfficial(row);
      const team = row.regional_team;
      return {
        ...official,
        teamId: team?.id || row.regional_team_id,
        teamName: team?.team_name || 'Unknown Team',
        teamRegion: team?.region || null,
        teamStatus: team?.registration_status || null,
      };
    });
  },

  async getOfficials(regionalTeamId) {
    const { data, error } = await supabase
      .from('regional_team_officials')
      .select('*')
      .eq('regional_team_id', regionalTeamId)
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load regional team officials');
    return (data || []).map(normalizeOfficial);
  },

  async deletePlayer(playerId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!playerId) throw new Error('Player ID is required');

    const { error } = await supabase
      .from('regional_team_players')
      .delete()
      .eq('id', playerId);

    if (error) throw new Error(error.message || 'Failed to delete regional player');
    return true;
  },

  async deleteOfficial(officialId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!officialId) throw new Error('Official ID is required');

    const { error } = await supabase
      .from('regional_team_officials')
      .delete()
      .eq('id', officialId);

    if (error) throw new Error(error.message || 'Failed to delete regional official');
    return true;
  },

  async saveRoster(regionalTeamId, { players = [], officials = [] }) {
    if (players.length > MAX_PLAYERS) {
      throw new Error(`A regional team can have a maximum of ${MAX_PLAYERS} players.`);
    }
    if (officials.length > MAX_OFFICIALS) {
      throw new Error(`A regional team can have a maximum of ${MAX_OFFICIALS} other team officials.`);
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: deletePlayersError } = await supabase
      .from('regional_team_players')
      .delete()
      .eq('regional_team_id', regionalTeamId);
    if (deletePlayersError) {
      throw new Error(deletePlayersError.message || 'Failed to clear existing players');
    }

    const { error: deleteOfficialsError } = await supabase
      .from('regional_team_officials')
      .delete()
      .eq('regional_team_id', regionalTeamId);
    if (deleteOfficialsError) {
      throw new Error(deleteOfficialsError.message || 'Failed to clear existing officials');
    }

    for (const player of players) {
      const fullName = player.fullName?.trim();
      if (!fullName) continue;

      const age =
        player.age != null && player.age !== ''
          ? parseInt(player.age, 10)
          : null;

      const { data: insertedPlayer, error: playerError } = await supabase
        .from('regional_team_players')
        .insert({
          regional_team_id: regionalTeamId,
          full_name: fullName,
          position: player.position || null,
          date_of_birth: player.dateOfBirth || null,
          age: Number.isFinite(age) ? age : null,
          email: player.email?.trim() || null,
          phone: player.phone?.trim() || null,
        })
        .select()
        .single();

      if (playerError) {
        throw new Error(playerError.message || `Failed to save player ${fullName}`);
      }

      const updates = {};

      if (player.photoFile) {
        const uploaded = await uploadRosterFile(
          player.photoFile,
          regionalTeamId,
          `players/${insertedPlayer.id}/photo`,
          IMAGE_MIME_TYPES,
          5 * 1024 * 1024,
          'Player photo'
        );
        if (uploaded) {
          updates.photo_url = uploaded.url;
          updates.photo_path = uploaded.path;
        }
      } else if (player.photoUrl) {
        updates.photo_url = player.photoUrl;
        if (player.photoPath) updates.photo_path = player.photoPath;
      }

      if (player.birthCertificateFile) {
        const uploaded = await uploadRosterFile(
          player.birthCertificateFile,
          regionalTeamId,
          `players/${insertedPlayer.id}/documents`,
          DOCUMENT_MIME_TYPES,
          10 * 1024 * 1024,
          'Birth certificate'
        );
        if (uploaded) {
          updates.birth_certificate_url = uploaded.url;
          updates.birth_certificate_path = uploaded.path;
        }
      } else if (player.birthCertificateUrl) {
        updates.birth_certificate_url = player.birthCertificateUrl;
        if (player.birthCertificatePath) {
          updates.birth_certificate_path = player.birthCertificatePath;
        }
      }

      if (Object.keys(updates).length > 0) {
        const { error: updateError } = await supabase
          .from('regional_team_players')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', insertedPlayer.id);

        if (updateError) {
          console.error('Failed to save player files:', updateError);
        }
      }
    }

    const officialsToSave = officials.filter((official) => official.fullName?.trim());
    if (officialsToSave.length > 0) {
      const { error: officialsError } = await supabase.from('regional_team_officials').insert(
        officialsToSave.map((official) => ({
          regional_team_id: regionalTeamId,
          full_name: official.fullName.trim(),
          role_title: official.roleTitle?.trim() || null,
          email: official.email?.trim() || null,
          phone: official.phone?.trim() || null,
        }))
      );
      if (officialsError) throw new Error(officialsError.message || 'Failed to save officials');
    }

    return true;
  },
};
